import { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { ArrowRightIcon, ButtonV2, FloatingToolbar, IconButtonV2 } from '@utilities';
import SaveAsTemplate from '../../../../../../components/SaveAsTemplate';

import styles from './_index.module.scss';

const getScrollableAncestor = (startElement) => {
  let element = startElement?.parentElement;

  while (element) {
    const { overflow, overflowY } = window.getComputedStyle(element);
    const allowsVerticalScroll =
      ['auto', 'scroll'].includes(overflowY) || ['auto', 'scroll'].includes(overflow);

    if (allowsVerticalScroll && element.scrollHeight > element.clientHeight) {
      return element;
    }

    element = element.parentElement;
  }

  return null;
};

const CanvasFloatingToolbar = ({
  canAccessChat,
  onSelectPrompts,
  publicToken,
  selectPromptsDisabled,
  selectPromptsLabel = 'Select Prompts',
  selectPromptsLoading = false,
  topicIds = [],
}) => {
  const layerRef = useRef(null);
  const [layerStyle, setLayerStyle] = useState(null);

  useLayoutEffect(() => {
    const layerElement = layerRef.current;
    if (!layerElement) return undefined;

    const parentElement = layerElement.parentElement;
    if (!parentElement) return undefined;

    const updatePosition = () => {
      const { left, width } = parentElement.getBoundingClientRect();

      setLayerStyle((prev) => {
        if (prev?.left === left && prev?.width === width) {
          return prev;
        }

        return { left, width };
      });
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(parentElement);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      resizeObserver?.disconnect();
    };
  }, []);

  const handleScrollToTop = () => {
    const scrollContainer = getScrollableAncestor(layerRef.current);

    if (scrollContainer) {
      scrollContainer.scrollTo({ behavior: 'smooth', top: 0 });
      return;
    }

    if (document.scrollingElement) {
      document.scrollingElement.scrollTo({ behavior: 'smooth', top: 0 });
      return;
    }

    window.scrollTo({ behavior: 'smooth', top: 0 });
  };

  return (
    <div
      className={styles['canvas-floating-toolbar-fixed-layer']}
      ref={layerRef}
      style={layerStyle || undefined}
    >
      <div className={styles['canvas-floating-toolbar-container']}>
        <FloatingToolbar>
          <div className={styles['canvas-floating-toolbar-content']}>
            <IconButtonV2
              aria-label="Scroll to top"
              className={styles['canvas-floating-toolbar-scroll-button']}
              icon={<ArrowRightIcon />}
              onClick={handleScrollToTop}
            />
            <SaveAsTemplate
              canAccessChat={canAccessChat}
              publicToken={publicToken}
              topicIds={topicIds}
            />
            <ButtonV2
              disabled={selectPromptsDisabled}
              isLoading={selectPromptsLoading}
              onClick={onSelectPrompts}
              shape="round"
              variant="primary"
            >
              {selectPromptsLabel}
            </ButtonV2>
          </div>
        </FloatingToolbar>
      </div>
    </div>
  );
};

CanvasFloatingToolbar.propTypes = {
  canAccessChat: PropTypes.bool,
  onSelectPrompts: PropTypes.func,
  publicToken: PropTypes.string,
  selectPromptsDisabled: PropTypes.bool,
  selectPromptsLabel: PropTypes.string,
  selectPromptsLoading: PropTypes.bool,
  topicIds: PropTypes.arrayOf(PropTypes.string),
};

export default CanvasFloatingToolbar;
